import React, { useState, useEffect, useRef } from 'react';
import { Book, BookStatus } from '../objects/Book';

interface BookListProps {
  books: Book[];
  emptyMessage?: string;
  onSelect: (book: Book) => void;
  onStatusChange: (book: Book, status: BookStatus) => void;
  onReorder?: (reorderedBooks: Book[]) => void;
}

const statusOptions: BookStatus[] = ['in queue', 'reading', 'occasional reference', 'done', 'abandoned', 'probably not', "won't read"];

const statusClass = (status: BookStatus): string => {
  switch (status) {
    case 'in queue':
      return 'in-queue';
    case 'reading':
      return 'reading';
    case 'occasional reference':
      return 'occasional-reference';
    case 'done':
      return 'done';
    case 'abandoned':
      return 'abandoned';
    case 'probably not':
      return 'probably-not';
    case "won't read":
      return 'wont-read';
    default:
      return '';
  }
};

const BookList: React.FC<BookListProps> = ({
  books,
  emptyMessage = 'Your reading list is empty. Add your first book.',
  onSelect,
  onStatusChange,
  onReorder,
}) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const canReorder = books.length > 0 && books[0].status === 'in queue' && !!onReorder;

  useEffect(() => {
    if (openMenuIndex === null) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuIndex]);

  const moveItem = (fromIndex: number, toIndex: number) => {
    if (!onReorder || fromIndex === toIndex) return;
    const reordered = [...books];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onReorder(reordered);
    setOpenMenuIndex(null);
  };

  if (!books.length) {
    return <div className="empty-state">{emptyMessage}</div>;
  }

  return (
    <div className="book-list">
      {books.map((book, index) => (
        <div className="book-row" key={book.uuid}>
          {canReorder && (
            <div className="position-indicator" ref={openMenuIndex === index ? menuRef : undefined}>
              <button
                className="position-button"
                onClick={() => setOpenMenuIndex(openMenuIndex === index ? null : index)}
                title="Reorder"
              >
                {index + 1}
              </button>
              {openMenuIndex === index && (
                <div className="position-menu">
                  <button
                    className="position-menu-item"
                    disabled={index === 0}
                    onClick={() => moveItem(index, 0)}
                  >
                    Move to top
                  </button>
                  <button
                    className="position-menu-item"
                    disabled={index === 0}
                    onClick={() => moveItem(index, index - 1)}
                  >
                    Shift up
                  </button>
                  <button
                    className="position-menu-item"
                    disabled={index === books.length - 1}
                    onClick={() => moveItem(index, index + 1)}
                  >
                    Shift down
                  </button>
                  <button
                    className="position-menu-item"
                    disabled={index === books.length - 1}
                    onClick={() => moveItem(index, books.length - 1)}
                  >
                    Move to bottom
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="book-info" onClick={() => onSelect(book)} style={{ cursor: 'pointer' }}>
            <div className="book-title">{book.title}</div>
            <div className="book-meta">
              {book.author ? `by ${book.author}` : 'Author unknown'}{canReorder ? '' : ` · #${index + 1}`}
            </div>
          </div>

          <div className="top-actions">
            <select
              className={`status-select ${statusClass(book.status)}`}
              value={book.status}
              onChange={(event) => onStatusChange(book, event.target.value as BookStatus)}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <button
              className="button ghost small"
              onClick={() => {
                if (window.confirm('Hide this book from your list?')) {
                  onStatusChange(book, "won't read");
                }
              }}
            >
              Hide
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BookList;
